import {useFluxStore} from 'client/framework';
import {useCallback, useMemo} from 'react';
import type {List} from 'immutable';

import {
  getClassroomQuery,
  getCoteachersQuery,
  getInvitationsQuery,
  getStudentsQuery
} from 'client/Classrooms/Classroom/Teacher/People/people.queries';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';
import {invalidatePartialInterest} from 'resources/mandark.resource';

// Co-teachers render client-side, so that query fetches whole. The students/invitations
// queries are kept only for the reused mutation flows (row actions + bulk-resend via
// ManageRosterContext) — the enrolled/invited lists are server-paged separately (useRosterPagination).
const FULL_FETCH_PAGE_SIZE = 1000;

// Minimal legacy-Mandark model shapes — only the getters the People views read. Mirrors the
// `as unknown as` pattern in useClassSettings rather than pulling full @albert-io/models types.
interface EmailDomain {
  getName: () => string;
}

export interface RosterSchool {
  getId: () => string;
  getName: () => string;
  isHardCap: () => boolean;
  getEmailDomains: () => List<EmailDomain> | undefined;
}

export interface RosterClassroom {
  getId: () => string;
  getName: () => string;
  isLicensed: () => boolean;
  getSchool: () => RosterSchool;
  getMeta: () => {getCountOfSchoolLicenseSeatsRemaining: () => number};
  // Roster empty states read these totals (see people.queries classroom `counts` meta context)
  // instead of the full Lists, which are no longer fetched now that the lists are server-paged.
  getCountOfStudents: () => number;
  getCountOfPendingClassroomInvitations: () => number;
}

export interface RosterStudent {
  getId: () => string;
  getFirstName: () => string;
  getLastName: () => string;
  getEmail: () => string;
  getMeta: () => {isManagedUser: () => boolean};
}

export interface RosterInvitation {
  getId: () => string;
  getEmail: () => string;
  getSentEmailAt: () => {format: (token: string) => string} | null;
  getUpdatedAt: () => {format: (token: string) => string} | null;
}

export interface RosterTeacher {
  getId: () => string;
  getDisplayName: () => string;
  getEmail: () => string;
}

/** The legacy ManageRosterContext value the reused roster modals/dropdowns read. */
export interface ManageRosterContextValue {
  queries: {classroom: unknown; invitations: unknown; students: unknown};
  classroom: RosterClassroom | null;
  invitations: List<RosterInvitation>;
  students: List<RosterStudent>;
  isReady: boolean;
  modalToShow: string;
  setIsReady: (value: boolean) => void;
  setModalToShow: (value: string) => void;
}

/** The legacy PeopleContext value AddCoteacher / RemoveCoteacher read. */
export interface PeopleContextValue {
  classroom: RosterClassroom | null;
  queries: {coteachers: unknown};
  currentTeacherId: string;
}

export interface PeopleRoster {
  isReady: boolean;
  hasError: boolean;
  classroom: RosterClassroom | null;
  coteachers: List<RosterTeacher>;
  currentTeacherId: string;
  /** Hard refetch that drops isReady (spinner) — for retrying a failed load. */
  reload: () => void;
  /** In-place refetch that keeps the view mounted — for reused-flow mutations (no "page refresh"). */
  softReload: () => void;
  /** Same query instances the reused modals/dropdowns invalidate, so their mutations refresh this view. */
  queries: {classroom: unknown; invitations: unknown; students: unknown; coteachers: unknown};
}

/**
 * Loads the per-class roster (students, pending invitations, co-teachers + the classroom's license
 * seat meta) from the legacy Mandark JSON:API —
 */
export const usePeopleRoster = (classroomId: string): PeopleRoster => {
  const currentTeacherId = useFluxStore(
    masqueradeStore,
    (s) => s.getUserIdByMasqueradeState() ?? ''
  );

  const queries = useMemo(
    () => ({
      classroom: getClassroomQuery(classroomId),
      invitations: getInvitationsQuery(classroomId).pageSize(FULL_FETCH_PAGE_SIZE),
      students: getStudentsQuery(classroomId).pageSize(FULL_FETCH_PAGE_SIZE),
      coteachers: getCoteachersQuery(classroomId).pageSize(FULL_FETCH_PAGE_SIZE)
    }),
    [classroomId]
  );

  // The enrolled/invited lists page themselves (useRosterPagination), so only the classroom (for its
  // license + count meta) and the small co-teacher list gate the tab's ready/skeleton state.
  const {isReady, invalidateQueries, errors} = useAreLatestQueriesReady([
    queries.classroom,
    queries.coteachers
  ]);

  // Bridged Mandark reads: getResource() re-runs on every state-tree emit, so a reused-flow
  // mutation (remove student, resend, add co-teacher…) re-dirties the interest and the re-read
  // triggers the refetch. A bare render-time read would be frozen by the React Compiler
  // (useStoreChangePump's re-render can't reach compiler-memoized reads).
  const classroomResource = useFluxStore(masqueradeStore, () =>
    queries.classroom.getResource()
  ) as RosterClassroom;
  const classroom = isReady ? classroomResource : null;
  const coteachers = useFluxStore(masqueradeStore, () =>
    queries.coteachers.getResource()
  ) as List<RosterTeacher>;

  // Re-dirty the interests so the bridged reads refetch WITHOUT dropping isReady —
  // unlike reload(), which flips isReady false and remounts the whole view behind a spinner.
  const softReload = useCallback(() => {
    [queries.classroom, queries.invitations, queries.students, queries.coteachers].forEach(
      (query) => invalidatePartialInterest(query.done())
    );
    // The enrolled/invited lists page with their own query params (page/sort/search), so the
    // param-scoped invalidations above miss them (invalidation intersects on every query key).
    // Invalidate the collections by resourcePath alone — which matches ALL page/sort/search variants
    // — so a reused-flow mutation (remove student, resend/revoke invite) refreshes the list right away.
    invalidatePartialInterest({resourcePath: queries.students.done().resourcePath});
    invalidatePartialInterest({resourcePath: queries.invitations.done().resourcePath});
  }, [queries]);

  return {
    isReady,
    hasError: errors.some(Boolean),
    classroom,
    coteachers,
    currentTeacherId,
    reload: invalidateQueries,
    softReload,
    queries
  };
};
